import { create } from "zustand";
import { immer } from "zustand/middleware/immer";
import { devtools } from "zustand/middleware";
import { SecurityService } from "../services/security.service";
import type { BlacklistItem, FraudConfigResponse } from "@codway-dev/shared";

// ---------- Helpers ----------

function blacklistToText(
  items: BlacklistItem[],
  type: BlacklistItem["type"]
): string {
  return items
    .filter((item) => item.type === type && item.isActive)
    .map((item) => item.value)
    .join("\n");
}

function textToBlacklist(
  text: string,
  type: BlacklistItem["type"]
): BlacklistItem[] {
  return text
    .split("\n")
    .map((line) => line.trim())
    .filter(Boolean)
    .map((value) => ({ type, value, isActive: true }));
}

// ---------- Types ----------

interface SecurityEditorState {
  velocityEnabled: boolean;
  velocityLimit: number;
  velocityWindowHours: number;
  maxItemsEnabled: boolean;
  maxItemsPerOrder: number;

  blockedEmails: string;
  blockedPhones: string;
  blockedIps: string;

  initialSnapshot: string | null;
  isLoading: boolean;
  isSaving: boolean;
  error: string | null;
}

interface SecurityEditorActions {
  loadFromBackend: () => Promise<void>;
  updateField: <K extends keyof SecurityEditorState>(
    key: K,
    value: SecurityEditorState[K]
  ) => void;
  save: () => Promise<void>;
  discardChanges: () => void;
}

type SecurityEditorStore = SecurityEditorState & SecurityEditorActions;

// ---------- Snapshot ----------

function getSnapshot(state: SecurityEditorState): string {
  return JSON.stringify({
    velocityEnabled: state.velocityEnabled,
    velocityLimit: state.velocityLimit,
    velocityWindowHours: state.velocityWindowHours,
    maxItemsEnabled: state.maxItemsEnabled,
    maxItemsPerOrder: state.maxItemsPerOrder,
    blockedEmails: state.blockedEmails,
    blockedPhones: state.blockedPhones,
    blockedIps: state.blockedIps,
  });
}

function applyConfigToState(
  state: SecurityEditorState,
  data: FraudConfigResponse
) {
  state.velocityEnabled = data.settings.velocityEnabled;
  state.velocityLimit = data.settings.velocityLimit;
  state.velocityWindowHours = data.settings.velocityWindowHours;
  state.maxItemsEnabled = data.settings.maxItemsEnabled;
  state.maxItemsPerOrder = data.settings.maxItemsPerOrder;

  state.blockedEmails = blacklistToText(data.blacklists, "EMAIL");
  state.blockedPhones = blacklistToText(data.blacklists, "PHONE");
  state.blockedIps = blacklistToText(data.blacklists, "IP");
}

// ---------- Selector ----------

export const selectSecurityIsDirty = (state: SecurityEditorStore) => {
  if (!state.initialSnapshot) return false;
  return getSnapshot(state) !== state.initialSnapshot;
};

// ---------- Store ----------

export const useSecurityEditorStore = create<SecurityEditorStore>()(
  devtools(
    immer((set, get) => ({
      velocityEnabled: false,
      velocityLimit: 3,
      velocityWindowHours: 1,
      maxItemsEnabled: false,
      maxItemsPerOrder: 20,

      blockedEmails: "",
      blockedPhones: "",
      blockedIps: "",

      initialSnapshot: null,
      isLoading: false,
      isSaving: false,
      error: null,

      loadFromBackend: async () => {
        set((state) => {
          state.isLoading = true;
          state.error = null;
        });
        try {
          const data = await SecurityService.getConfig();
          set((state) => {
            applyConfigToState(state, data);
            state.initialSnapshot = getSnapshot(state);
            state.isLoading = false;
          });
        } catch (err) {
          set((state) => {
            state.error =
              err instanceof Error ? err.message : "Failed to load settings";
            state.isLoading = false;
          });
        }
      },

      updateField: (key, value) =>
        set((state) => {
          state[key] = value as never;
        }),

      save: async () => {
        set((state) => {
          state.isSaving = true;
        });
        try {
          const s = get();
          const blacklists: BlacklistItem[] = [
            ...textToBlacklist(s.blockedEmails, "EMAIL"),
            ...textToBlacklist(s.blockedPhones, "PHONE"),
            ...textToBlacklist(s.blockedIps, "IP"),
          ];

          const data = await SecurityService.updateConfig({
            settings: {
              velocityEnabled: s.velocityEnabled,
              velocityLimit: s.velocityLimit,
              velocityWindowHours: s.velocityWindowHours,
              maxItemsEnabled: s.maxItemsEnabled,
              maxItemsPerOrder: s.maxItemsPerOrder,
            },
            blacklists,
          });

          set((state) => {
            applyConfigToState(state, data);
            state.initialSnapshot = getSnapshot(state);
            state.isSaving = false;
          });
        } catch {
          set((state) => {
            state.isSaving = false;
          });
          throw new Error("Error al guardar la configuración de seguridad");
        }
      },

      discardChanges: () =>
        set((state) => {
          if (state.initialSnapshot) {
            const initial = JSON.parse(state.initialSnapshot);
            Object.assign(state, initial);
          }
        }),
    })),
    { name: "SecurityEditorStore" }
  )
);
